import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "../lib/api";
import { useToast } from "../lib/toast";
import { cx, Spinner } from "./ui";
import { BrandBlock } from "./Layout";
import { functionStatus } from "../lib/functionStatus";
import {
  FUNCTION_META,
  TONE_CLASSES,
  GridIcon,
  LogOutIcon,
  ArrowLeftIcon,
  LinkIcon,
} from "./ui/Icons";

/**
 * Import types, in the order a migration is actually worked through: the
 * things transactions refer to first, then the transactions themselves.
 *
 * Contacts, Customers and Vendors overlap on purpose: Contacts loads both
 * sides in one sheet via its IsCustomer / IsSupplier columns, while Customers
 * and Vendors take a separate sheet each. Use one route or the other for a
 * given record — loading the same person through both creates them twice in
 * Naqood, since they're matched by name and nothing dedupes across the two.
 */
export const CATALOGUE = [
  {
    group: "Setup",
    hint: "Load these first — transactions refer to them",
    types: ["accounts", "bank-accounts", "taxes", "contacts", "customers", "vendors", "items"],
  },
  {
    group: "Transactions",
    hint: "The day-to-day entries",
    types: [
      "invoices",
      "purchases",
      "payments",
      "bill-payments",
      "spend-money",
      "receive-money",
      "journals",
      "journal-transfer",
    ],
  },
];

export const ALL_TYPES = CATALOGUE.flatMap((g) => g.types);

/**
 * Sidebar shown inside a file: brand, which file you're in, then every import
 * type. Switching between imports never leaves the page.
 */
export default function FileSidebar({ file, user, activeType, fileId }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [functions, setFunctions] = useState([]);
  const [opening, setOpening] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/api/file-functions/${fileId}/function`);
      setFunctions(res.data);
    } catch {
      /* the sidebar is navigation — a failure here shouldn't block the page */
    }
  }, [fileId]);

  useEffect(() => {
    load();
  }, [load]);

  // Newest record wins per type, so historic duplicates stay hidden.
  const byType = {};
  for (const fn of [...functions].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  )) {
    byType[fn.type] = fn;
  }

  const open = async (type) => {
    if (type === activeType) return;
    setOpening(type);
    try {
      const res = await api.post(`/api/file-functions/${fileId}/function`, { type });
      navigate(`/file/${fileId}/function/${res.data._id}`);
    } catch (err) {
      toast.error("Could not open", errMsg(err));
    } finally {
      setOpening(null);
    }
  };

  const initials = (user?.name || "?")
    .split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  const doneCount = ALL_TYPES.filter(
    (t) => functionStatus(byType[t]).key === "pushed"
  ).length;

  return (
    <aside className="sticky top-0 flex h-screen w-[276px] shrink-0 flex-col bg-gradient-to-b from-ink-900 via-ink-900 to-ink-950">
      <BrandBlock />

      {/* Current file */}
      <div className="border-b border-white/10 px-4 py-3.5">
        <button
          onClick={() => navigate("/dashboard")}
          className="mb-2 flex items-center gap-1.5 text-[11.5px] font-medium text-ink-400 transition hover:text-white"
        >
          <ArrowLeftIcon size={12} />
          All files
        </button>
        <p className="truncate text-[14px] font-semibold text-white">
          {file?.fileName || "—"}
        </p>
        {file?.naqood?.organizationSlug && (
          <p className="mt-0.5 flex items-center gap-1 truncate font-mono text-[11px] text-ink-500">
            <LinkIcon size={11} className="shrink-0" />
            {file.naqood.organizationSlug}
          </p>
        )}
      </div>

      {/* Progress */}
      <div className="flex items-center justify-between px-5 pb-1 pt-3">
        <span className="text-[10.5px] font-semibold uppercase tracking-widest text-ink-600">
          Imports
        </span>
        <span className="text-[11px] font-semibold tabular-nums text-ink-500">
          {doneCount}/{ALL_TYPES.length}
        </span>
      </div>

      {/* Catalogue */}
      <nav className="flex-1 space-y-3 overflow-y-auto px-3 pb-3 pt-1">
        {CATALOGUE.map(({ group, types }) => (
          <div key={group}>
            <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-widest text-ink-600/90">
              {group}
            </p>
            <div className="space-y-0.5">
              {types.map((type) => {
                const meta = FUNCTION_META[type] || { Icon: GridIcon, tone: "slate", label: type };
                const { Icon, tone, label } = meta;
                const active = type === activeType;
                const status = functionStatus(byType[type]);
                const busy = opening === type;

                return (
                  <button
                    key={type}
                    onClick={() => open(type)}
                    className={cx(
                      "group relative flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium transition",
                      active
                        ? "bg-gradient-to-r from-brand-500/20 to-transparent text-white ring-1 ring-inset ring-brand-500/30"
                        : "text-ink-400 hover:bg-white/[0.06] hover:text-ink-100"
                    )}
                  >
                    {active && (
                      <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-brand-400" />
                    )}
                    <span
                      className={cx(
                        "grid h-6 w-6 shrink-0 place-items-center rounded-md ring-1 transition",
                        active
                          ? TONE_CLASSES[tone]
                          : "bg-white/[0.06] text-ink-500 ring-white/10 group-hover:text-ink-300"
                      )}
                    >
                      {busy ? <Spinner size={12} /> : <Icon size={13} />}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-left">{label}</span>

                    {/* Status is a quiet dot here, not a number — the counts
                        belong on the function page, not in navigation. Hover
                        gives the detail. */}
                    {status.key !== "empty" && (
                      <span
                        title={status.label}
                        className={cx(
                          "h-1.5 w-1.5 shrink-0 rounded-full",
                          status.key === "pushed"
                            ? "bg-emerald-400"
                            : status.key === "partial"
                            ? "bg-amber-400"
                            : "bg-ink-500"
                        )}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* User */}
      <div className="border-t border-white/10 p-3">
        <div className="mb-1 flex items-center gap-2.5 rounded-lg px-2 py-1.5">
          <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gradient-to-br from-brand-400 to-brand-600 text-[12px] font-bold text-white shadow-lg shadow-brand-500/20">
            {initials}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-[12.5px] font-medium leading-tight text-white">
              {user?.name || "—"}
            </p>
            <p className="truncate text-[11px] leading-tight text-ink-500">
              {user?.email || ""}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            localStorage.clear();
            navigate("/");
          }}
          className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-[13px] font-medium text-ink-400 transition hover:bg-red-500/10 hover:text-red-300"
        >
          <LogOutIcon size={15} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
